/**
 * EvidenceModeControl — Evidence_Setup_Auto_And_Modes ES3.
 *
 * A small three-state segmented control surfacing (and editing)
 * `SiteEntity.evidenceMode`:
 *
 *   - `'awaiting'`      — freshly created; the user hasn't decided yet.
 *   - `'ai-only'`       — runs on the Datacrate Engine alone (domain
 *                         model + curated KB + NRC ADAMS + provider data +
 *                         AI research). 0% doc coverage is intentional.
 *   - `'docs-included'` — the user has uploaded ≥1 document and wants the
 *                         platform to weight it.
 *
 * Mounted on the Site-Landing Overview tab so a user can switch mode
 * post-creation. Switching shows a confirmation toast and triggers a
 * readiness recompute (via `store.setSiteEvidenceMode`).
 *
 * Presentation-only beyond the single store call — every behaviour is
 * driven off `site.evidenceMode`, so it's mountable in isolation for tests.
 */
import React from 'react';
import { Sparkles, FileText, HelpCircle } from 'lucide-react';
import { toast } from 'sonner';
import type { SiteEntity } from '../../types/site';
import { useStore } from '../../store';
import { InfoTooltip } from '../common/InfoTooltip';

type Mode = NonNullable<SiteEntity['evidenceMode']>;

const MODE_META: Record<Mode, { label: string; icon: React.ReactNode; blurb: string }> = {
  awaiting: {
    label: 'Awaiting',
    icon: <HelpCircle className="w-3 h-3" aria-hidden />,
    blurb: "Not decided yet — pick a mode below. Until then this site is treated as freshly created.",
  },
  'ai-only': {
    label: 'Datacrate Engine',
    icon: <Sparkles className="w-3 h-3" aria-hidden />,
    blurb: 'Runs on the Datacrate Engine: our domain model + curated knowledge base + NRC ADAMS + government provider data + AI research synthesis. Uploads can be stored for later but stay inactive until you switch to Docs included.',
  },
  'docs-included': {
    label: 'Docs included',
    icon: <FileText className="w-3 h-3" aria-hidden />,
    blurb: 'Your uploaded documents are weighted into evidence alongside the Datacrate Engine — our domain model, curated KB, and provider data.',
  },
};

const ORDER: readonly Mode[] = ['awaiting', 'ai-only', 'docs-included'];

interface Props {
  site: SiteEntity;
  /** Override the store call — for tests / Storybook. */
  onChange?: (mode: Mode) => void | Promise<void>;
  /**
   * Condensed Aurora variant (founder QA r3 item 5a — Documents-tab top
   * row): the inline explainer paragraph collapses into an ⓘ tooltip so
   * the control reads as one slim row. The full variant (Site Evidence
   * tab) keeps the visible blurb.
   */
  condensed?: boolean;
}

export const EvidenceModeControl: React.FC<Props> = ({ site, onChange, condensed = false }) => {
  const setSiteEvidenceMode = useStore(s => s.setSiteEvidenceMode);
  // DAE3 — legacy sites without an explicit mode default to the platform's
  // signature stack (Datacrate Engine). 'awaiting' remains a real state
  // users can pick, but it requires an explicit click — no surprise empty
  // default for sites pre-dating the evidenceMode field.
  const current: Mode = site.evidenceMode ?? 'ai-only';

  const handlePick = async (next: Mode) => {
    if (next === current) return;
    try {
      if (onChange) await onChange(next);
      else await setSiteEvidenceMode(site.id, next);
      toast.success(`Evidence mode set to "${MODE_META[next].label}".`);
    } catch (e) {
      toast.error(`Couldn't change evidence mode: ${(e as Error)?.message ?? 'unknown error'}`);
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-2" data-testid="evidence-mode-control" data-mode={current}>
      <span className="type-section text-text-secondary">Evidence mode</span>
      {/* overflow-hidden clips the active segment's accent fill to the
          wrapper's Aurora radius (flush segments stay square inside). */}
      <div className="inline-flex border border-border rounded-sm overflow-hidden" role="group" aria-label="Evidence mode">
        {ORDER.map((m, i) => {
          const active = m === current;
          return (
            <button
              key={m}
              type="button"
              onClick={() => void handlePick(m)}
              aria-pressed={active}
              data-testid={`evidence-mode-option-${m}`}
              title={MODE_META[m].blurb}
              className={`inline-flex items-center gap-1 px-2.5 py-1.5 type-section transition-colors ${
                i > 0 ? 'border-l border-border' : ''
              } ${
                active
                  ? 'selected-accent'
                  : 'text-text-secondary hover:text-text-primary hover:bg-state-hover'
              }`}
            >
              {MODE_META[m].icon}
              {MODE_META[m].label}
            </button>
          );
        })}
      </div>
      {condensed ? (
        <InfoTooltip
          text={MODE_META[current].blurb}
          testId="evidence-mode-info"
        />
      ) : (
        <p className="text-[11px] text-text-muted leading-relaxed max-w-xl" data-testid="evidence-mode-blurb">
          {MODE_META[current].blurb}
        </p>
      )}
    </div>
  );
};
